import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import { marked } from "marked"
import usePrefersReducedMotion from "hooks/usePrefersReducedMotion"
import BotAvatarIcon from "./bot-avatar.inline.svg"
import * as css from "./ChatbotDemo.module.css"

marked.setOptions({ breaks: true, gfm: true })

// ── SVG Icons ───────────────────────────────────────────────────────────────

const ArrowUpIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M868 545.5L536.1 163a31.96 31.96 0 00-48.3 0L156 545.5a7.97 7.97 0 006 13.2h81c4.6 0 9-2 12.1-5.5L474 300.9V864c0 4.4 3.6 8 8 8h60c4.4 0 8-3.6 8-8V300.9l218.9 252.3c3 3.5 7.4 5.5 12.1 5.5h81c6.8 0 10.5-8 6-13.2z" />
  </svg>
)

const UserIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 272c44.2 0 80 35.8 80 80s-35.8 80-80 80-80-35.8-80-80 35.8-80 80-80zm0 462c-79.5 0-152.4-38.7-197.2-100.8 3.7-67.5 133.7-104.7 197.2-104.7s193.5 37.2 197.2 104.7C664.4 759.3 591.5 798 512 798z" />
  </svg>
)

const ExpandIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M855 160.1l-189.2 23.5c-6.6.8-9.3 8.8-4.7 13.5l54.7 54.7-153.5 153.5a8.03 8.03 0 000 11.3l45.1 45.1c3.1 3.1 8.2 3.1 11.3 0l153.6-153.6 54.7 54.7a7.94 7.94 0 0013.5-4.7L863.9 169a7.9 7.9 0 00-8.9-8.9zM416.6 562.3a8.03 8.03 0 00-11.3 0L251.8 715.9l-54.7-54.7a7.94 7.94 0 00-13.5 4.7L160.1 855c-.6 5.2 3.7 9.5 8.9 8.9l189.2-23.5c6.6-.8 9.3-8.8 4.7-13.5l-54.7-54.7 153.6-153.6c3.1-3.1 3.1-8.2 0-11.3l-45.2-45z" />
  </svg>
)

const CloseIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L512 449.8 295.9 191.7c-3-3.6-7.5-5.7-12.3-5.7H203.8c-6.8 0-10.5 7.9-6.1 13.1L460.2 512 197.7 824.9A7.95 7.95 0 00203.8 838h79.8c4.7 0 9.2-2.1 12.3-5.7L512 574.1l216.2 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" />
  </svg>
)

const CodeIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M516 673c0 4.4 3.4 8 7.5 8h185c4.1 0 7.5-3.6 7.5-8v-48c0-4.4-3.4-8-7.5-8h-185c-4.1 0-7.5 3.6-7.5 8v48zm-194.9 6.1l192-161c3.8-3.2 3.8-9.1 0-12.3l-192-160.9A7.95 7.95 0 00308 351v62.7c0 2.4 1 4.6 2.9 6.1L420.7 512l-109.8 92.2a8.1 8.1 0 00-2.9 6.1V673c0 6.8 7.9 10.5 13.1 6.1zM880 112H144c-17.7 0-32 14.3-32 32v736c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V144c0-17.7-14.3-32-32-32zm-40 728H184V184h656v656z" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm193.5 301.7l-210.6 292a31.8 31.8 0 01-51.7 0L318.5 484.9c-3.8-5.3 0-12.7 6.5-12.7h46.9c10.2 0 19.9 4.9 25.9 13.3l71.2 98.8 157.2-218c6-8.3 15.6-13.3 25.9-13.3H699c6.5 0 10.3 7.4 6.5 12.7z" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z" />
  </svg>
)

const ArrowRightIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
)

const LoadingStopIcon = () => (
  <svg
    color="currentColor"
    viewBox="0 0 1000 1000"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <rect
      fill="currentColor"
      height="250"
      rx="24"
      ry="24"
      width="250"
      x="375"
      y="375"
    />
    <circle
      cx="500"
      cy="500"
      fill="none"
      r="450"
      stroke="currentColor"
      strokeWidth="100"
      opacity="0.45"
    />
    <circle
      cx="500"
      cy="500"
      fill="none"
      r="450"
      stroke="currentColor"
      strokeWidth="100"
      strokeDasharray="600 9999999"
    >
      <animateTransform
        attributeName="transform"
        dur="1s"
        from="0 500 500"
        repeatCount="indefinite"
        to="360 500 500"
        type="rotate"
      />
    </circle>
  </svg>
)

const LineChartIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M888 792H200V168c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v688c0 4.4 3.6 8 8 8h752c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zM305.8 637.7c3.1 3.1 8.1 3.1 11.3 0l138.3-137.6L583 628.5c3.1 3.1 8.2 3.1 11.3 0l275.4-275.3c3.1-3.1 3.1-8.2 0-11.3l-39.6-39.6a8.03 8.03 0 00-11.3 0l-230 229.9L461.4 404a8.03 8.03 0 00-11.3 0L266.3 586.7a8.03 8.03 0 000 11.3l39.5 39.7z" />
  </svg>
)

const ExclamationIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm-32 232c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v272c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V296zm32 440a48.01 48.01 0 010-96 48.01 48.01 0 010 96z" />
  </svg>
)

const SqlIcon = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" aria-hidden="true">
    <path d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 632H136V232h752v560zM396 482H204c-4.4 0-8 3.6-8 8v48c0 4.4 3.6 8 8 8h192c4.4 0 8-3.6 8-8v-48c0-4.4-3.6-8-8-8zm-34-107L264.4 472.6a8.03 8.03 0 000 11.3l22.6 22.6c3.1 3.1 8.2 3.1 11.3 0L396 408.7c3.1-3.1 3.1-8.2 0-11.3L362 363a8.03 8.03 0 00-11.3 0z" />
  </svg>
)

const CHIP_ICONS = {
  "line-chart": LineChartIcon,
  exclamation: ExclamationIcon,
  sql: SqlIcon,
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function makeSleeper(cancelRef) {
  return async function sleep(ms) {
    const CHUNK = 50
    for (let t = 0; t < ms; t += CHUNK) {
      if (cancelRef.current) return
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(CHUNK, ms - t))
      )
    }
  }
}

// ── Sub-components ──────────────────────────────────────────────────────────

function ReasoningStep({ steps }) {
  const [visibleCount, setVisibleCount] = useState(0)
  const [collapsed, setCollapsed] = useState(false)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let timer
    if (visibleCount < steps.length) {
      timer = setTimeout(() => setVisibleCount((v) => v + 1), 400)
    } else if (!done) {
      setDone(true)
      timer = setTimeout(() => setCollapsed(true), 900)
    }
    return () => clearTimeout(timer)
  }, [visibleCount, steps.length, done])

  return (
    <div className={css.reasoning}>
      <button
        type="button"
        className={css.reasoningHeader}
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <span className={css.reasoningIcon}>
          {done ? (
            <CheckCircleIcon />
          ) : (
            <span className={css.reasoningSpinner}>
              <span className={css.reasoningSpinnerDot} />
              <span className={css.reasoningSpinnerDot} />
              <span className={css.reasoningSpinnerDot} />
            </span>
          )}
        </span>
        <span className={css.reasoningLabel}>Reasoning</span>
        <span
          className={css.reasoningChevron}
          style={{
            transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)",
          }}
        >
          <ChevronDownIcon />
        </span>
      </button>
      {!collapsed && (
        <ul className={css.reasoningSteps}>
          {steps.slice(0, visibleCount).map((step, i) => (
            <li key={i} className={css.reasoningStep}>
              {step}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

ReasoningStep.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string).isRequired,
}

function TypingIndicator() {
  return (
    <div className={css.typingIndicator}>
      <div className={`${css.messageAvatar} ${css.botAvatar}`}>
        <BotAvatarIcon />
      </div>
      <div className={css.typingDots}>
        <span className={css.typingDot} />
        <span className={css.typingDot} />
        <span className={css.typingDot} />
      </div>
    </div>
  )
}

function MessageBubble({ role, text, image, imageAlt, cta, reasoning, chips }) {
  const isUser = role === "user"
  const htmlContent = !isUser && text ? marked.parse(text) : null

  return (
    <div
      className={`${css.messageBubble} ${
        isUser ? css.userMessage : css.botMessage
      }`}
    >
      <div
        className={`${css.messageAvatar} ${
          isUser ? css.userAvatar : css.botAvatar
        }`}
      >
        {isUser ? <UserIcon /> : <BotAvatarIcon />}
      </div>
      <div className={css.bubbleColumn}>
        {reasoning && <ReasoningStep steps={reasoning} />}
        <div
          className={`${css.bubbleContent} ${
            isUser ? css.userBubbleContent : css.botBubbleContent
          }`}
        >
          {isUser ? (
            text
          ) : (
            <>
              {text && (
                <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
              )}
              {image && (
                <div className={css.chartImageWrap}>
                  <img
                    src={image}
                    alt={imageAlt || "Chart"}
                    className={css.chartImage}
                  />
                </div>
              )}
              {cta && (
                <div className={css.ctaButton} aria-disabled="true">
                  <span>{cta.label}</span>
                  <ArrowRightIcon />
                </div>
              )}
              {chips && chips.length > 0 && (
                <div className={css.quickActions}>
                  {chips.map((chip, i) => {
                    const Icon = chip.icon ? CHIP_ICONS[chip.icon] : null
                    return (
                      <div key={i} className={css.quickActionChip}>
                        {Icon && <Icon />}
                        <span>{chip.label}</span>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

MessageBubble.propTypes = {
  role: PropTypes.oneOf(["user", "bot"]).isRequired,
  text: PropTypes.string,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string,
  }),
  reasoning: PropTypes.arrayOf(PropTypes.string),
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.string,
    })
  ),
}

// ── Main Component ──────────────────────────────────────────────────────────

function ChatbotDemo({
  title = "Preset Chatbot",
  subtitle = "Online",
  placeholder = "Ask me about charts, dashboards, or data...",
  inputCtaTooltip = "Reach out to try the real Preset Chatbot",
  closingCtaText = "Ready to try this out **for real**? [Reach out to the Preset team](/contact-sales/) to get your hands on Preset Chatbot.",
  conversations = [],
  typingDelay = 1200,
  loop = false,
  loopDelay = 3500,
  onInputClick,
  className,
}) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const [visibleMessages, setVisibleMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [currentInput, setCurrentInput] = useState("")
  const [isVisible, setIsVisible] = useState(prefersReducedMotion)
  const [isFlipped, setIsFlipped] = useState(prefersReducedMotion)
  const messageListRef = useRef(null)
  const messageListInnerRef = useRef(null)
  const widgetRef = useRef(null)
  const cancelRef = useRef(false)
  const sleep = useRef(makeSleeper(cancelRef)).current

  // ─── Entrance: zoom-in back card → hold → flip to reveal chatbot ───
  // Skipped entirely when the user requests reduced motion — the widget
  // mounts already revealed and the autoplay below renders a static
  // final state.
  useEffect(() => {
    if (prefersReducedMotion) {
      setIsVisible(true)
      setIsFlipped(true)
      return
    }
    if (!widgetRef.current) return
    let flipTimer
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Stage 1: zoom-in + fade the back card (CSS transition, ~0.5s)
          setIsVisible(true)
          // Stage 2: hold for 0.5s, then flip
          // Wait for zoom-in (1s) + hold (0.5s) before flipping
          flipTimer = setTimeout(() => setIsFlipped(true), 1500)
          observer.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    observer.observe(widgetRef.current)
    return () => {
      observer.disconnect()
      clearTimeout(flipTimer)
    }
  }, [prefersReducedMotion])

  // Smooth-scroll to bottom whenever the inner content height changes.
  // A ResizeObserver on the inner wrapper catches EVERYTHING: new messages,
  // typing indicators, AND reasoning steps streaming in (which expand
  // inside an existing message without changing the visibleMessages array).
  useEffect(() => {
    const scrollEl = messageListRef.current
    const innerEl = messageListInnerRef.current
    if (!scrollEl || !innerEl) return
    const ro = new ResizeObserver(() => {
      scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: "smooth" })
    })
    ro.observe(innerEl)
    return () => ro.disconnect()
  }, [])

  // Append a message to the visible list (never clears — continuous history)
  const appendMessage = useCallback((msg) => {
    setVisibleMessages((v) => [...v, msg])
  }, [])

  // Replace the last message (used for reasoning → full response reveal)
  const replaceLastMessage = useCallback((msg) => {
    setVisibleMessages((v) => [...v.slice(0, -1), msg])
  }, [])

  // Stream user-message text into the input CTA one character at a time
  const streamInput = useCallback(
    async (text) => {
      for (let i = 1; i <= text.length; i++) {
        if (cancelRef.current) return
        setCurrentInput(text.slice(0, i))
        await sleep(3 + Math.random() * 3)
      }
    },
    [sleep]
  )

  // ─── Continuous auto-play ─────────────────────────────────────────────
  //
  // All conversations play sequentially into ONE continuous chat history.
  // Messages keep pushing upward, just like a real chat. Nothing clears.
  // After all conversations finish, a closing CTA bubble appears and
  // the demo stops — the user can scroll up through the whole history.
  useEffect(() => {
    if (!conversations.length || !isFlipped) return

    // Reduced motion: skip the streaming autoplay entirely and render a
    // static final state — welcome bubble plus the first conversation's
    // user message and full bot response (no reasoning stream, no typing
    // dots, no closing CTA loop). Gives the gist without animation.
    if (prefersReducedMotion) {
      const firstConv = conversations[0]
      const finalMessages = [
        {
          role: "bot",
          text: "You are viewing **Demo Dashboard**",
          chips: conversations.map((c) => ({
            label: c.chipLabel,
            icon: c.chipIcon,
          })),
        },
        ...(firstConv?.messages || []),
        { role: "bot", text: closingCtaText },
      ]
      setVisibleMessages(finalMessages)
      return
    }

    let mounted = true
    cancelRef.current = false

    async function play() {
      // In loop mode (e.g. ?loop on the home page for conference demos), the
      // entire script replays forever: clear history between iterations and
      // skip the closing CTA so the demo never lands on a static end-state.
      do {
        // Welcome bubble with decorative chips — then start typing immediately
        appendMessage({
          role: "bot",
          text: "You are viewing **Demo Dashboard**",
          chips: conversations.map((c) => ({
            label: c.chipLabel,
            icon: c.chipIcon,
          })),
        })

        // Tiny pause so the welcome bubble renders before typing starts
        await sleep(600)
        if (!mounted || cancelRef.current) return

        // Play each conversation sequentially — appending to the SAME list
        for (let ci = 0; ci < conversations.length; ci++) {
          const conv = conversations[ci]
          if (!mounted || cancelRef.current) return

          for (let mi = 0; mi < conv.messages.length; mi++) {
            const msg = conv.messages[mi]
            if (!mounted || cancelRef.current) return

            if (msg.role === "user") {
              // Stream text into the input area, then "send" it as a bubble
              await streamInput(msg.text)
              if (!mounted || cancelRef.current) return
              await sleep(350)
              appendMessage(msg)
              setCurrentInput("")
              await sleep(400)
            } else if (msg.reasoning) {
              // Bot message WITH reasoning:
              // 1) Show typing dots briefly
              setIsTyping(true)
              await sleep(800)
              if (!mounted || cancelRef.current) return
              setIsTyping(false)

              // 2) Append a reasoning-only placeholder (no text/image yet)
              appendMessage({
                role: "bot",
                reasoning: msg.reasoning,
              })

              // 3) Wait for reasoning steps to stream in + auto-collapse
              const reasoningTime = msg.reasoning.length * 400 + 1200
              await sleep(reasoningTime)
              if (!mounted || cancelRef.current) return

              // 4) Replace with full message (reasoning + text + image + cta)
              replaceLastMessage(msg)
              await sleep(2000)
            } else {
              // Bot message WITHOUT reasoning — simple typing dots → reveal
              setIsTyping(true)
              await sleep(typingDelay)
              if (!mounted || cancelRef.current) return
              setIsTyping(false)
              appendMessage(msg)
              await sleep(2000)
            }
          }

          // Minimal pause between conversations — keeps the flow feeling live
          if (ci < conversations.length - 1) {
            await sleep(300)
          }
        }

        if (loop) {
          // Pause on the final answer, then wipe and restart from the welcome
          await sleep(loopDelay)
          if (!mounted || cancelRef.current) return
          setVisibleMessages([])
          await sleep(400)
        } else {
          // After all conversations: closing CTA bubble
          if (mounted && !cancelRef.current) {
            await sleep(400)
            appendMessage({
              role: "bot",
              text: closingCtaText,
            })
          }
        }
      } while (loop && mounted && !cancelRef.current)
    }

    play()

    return () => {
      mounted = false
      cancelRef.current = true
    }
  }, [
    conversations,
    isFlipped,
    appendMessage,
    replaceLastMessage,
    streamInput,
    sleep,
    typingDelay,
    closingCtaText,
    loop,
    loopDelay,
    prefersReducedMotion,
  ])

  return (
    <div
      className={`${css.flipScene} ${isVisible ? css.flipSceneVisible : ""} ${className || ""}`}
      ref={widgetRef}
    >
      <div className={`${css.flipCard} ${isFlipped ? css.flipRevealed : ""}`}>
        {/* Back face — branded card shown before the flip */}
        <div className={css.flipBack}>
          <div className={css.flipBackInner}>
            <div className={css.flipBackAvatar}>
              <BotAvatarIcon />
            </div>
            <div className={css.flipBackTitle}>{title}</div>
            <div className={css.flipBackSubtitle}>{subtitle}</div>
          </div>
        </div>

        {/* Front face — the actual chatbot widget */}
        <div className={css.widget}>
          {/* Header */}
          <div className={css.header}>
            <div className={css.headerAvatar}>
              <BotAvatarIcon />
            </div>
            <div className={css.headerInfo}>
              <div className={css.headerTitle}>{title}</div>
              <div className={css.headerSubtitle}>
                <span className={css.onlineDot} />
                {subtitle}
              </div>
            </div>
            <div className={css.headerActions}>
              <CodeIcon />
              <ExpandIcon />
              <CloseIcon />
            </div>
          </div>

          {/* Message list — continuous, never clears.
              Click delegation: any /contact-sales/ link rendered inside a
              bubble (currently the closing-CTA bubble's markdown link) opens
              the same Contact Sales modal the input area opens, instead of
              navigating to the contact-sales page. */}
          <div
            className={css.messageList}
            ref={messageListRef}
            onClick={(event) => {
              if (!onInputClick) return
              const link = event.target.closest("a")
              if (!link) return
              const href = link.getAttribute("href") || ""
              if (href === "/contact-sales/" || href === "/contact-sales") {
                event.preventDefault()
                onInputClick(event)
              }
            }}
          >
            <div className={css.messageListInner} ref={messageListInnerRef}>
              {visibleMessages.map((msg, i) => (
                <MessageBubble
                  key={`msg-${i}`}
                  role={msg.role}
                  text={msg.text}
                  image={msg.image}
                  imageAlt={msg.imageAlt}
                  cta={msg.cta}
                  reasoning={msg.reasoning}
                  chips={msg.chips}
                />
              ))}
              {isTyping && <TypingIndicator />}
            </div>
          </div>

          {/* Input area — clickable CTA that opens Contact Sales modal */}
          <div className={css.inputArea}>
            <button
              type="button"
              className={`${css.inputCta} ${currentInput ? css.inputCtaTyping : ""}`}
              onClick={onInputClick}
              title={inputCtaTooltip}
            >
              <span className={css.inputCtaText}>
                {currentInput || placeholder}
              </span>
              <span className={css.inputCtaArrow}>
                {isTyping ? <LoadingStopIcon /> : <ArrowUpIcon />}
              </span>
            </button>
            <div className={css.inputCtaHint}>
              Try the real chatbot &mdash;{" "}
              <button
                type="button"
                className={css.inputCtaHintLink}
                onClick={onInputClick}
              >
                reach out to the team
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

ChatbotDemo.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  placeholder: PropTypes.string,
  inputCtaTooltip: PropTypes.string,
  closingCtaText: PropTypes.string,
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      chipLabel: PropTypes.string.isRequired,
      messages: PropTypes.array.isRequired,
    })
  ),
  typingDelay: PropTypes.number,
  loop: PropTypes.bool,
  loopDelay: PropTypes.number,
  onInputClick: PropTypes.func,
  className: PropTypes.string,
}

export default ChatbotDemo
